import React, { useState } from 'react';
import { ChevronDown, ChevronRight, MessageSquare, User } from 'lucide-react';

interface ChatItem {
  chat_id: string;
  timestamp: string;
  summary: string;
  persona_name: string;
  message_count: number;
}

interface DateGroup {
  date: string;
  display_date: string;
  chats: ChatItem[];
}

interface ChatListByDateProps {
  dates: DateGroup[];
  onChatSelect: (chatId: string) => void;
}

const ChatListByDate: React.FC<ChatListByDateProps> = ({ dates, onChatSelect }) => {
  const [expandedDates, setExpandedDates] = useState<Record<string, boolean>>(
    // Default to expanding the most recent date
    dates.length > 0 ? { [dates[0].date]: true } : {}
  );
  
  const toggleDate = (date: string) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  if (dates.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500">
        No chats found in the selected time period
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {dates.map(dateGroup => (
        <div key={dateGroup.date} className="border rounded-md overflow-hidden">
          <div 
            className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
            onClick={() => toggleDate(dateGroup.date)}
          >
            <h3 className="text-lg font-medium">{dateGroup.display_date}</h3>
            <div className="flex items-center">
              <span className="mr-2 text-sm text-gray-500">
                {dateGroup.chats.length} {dateGroup.chats.length === 1 ? 'chat' : 'chats'}
              </span>
              {expandedDates[dateGroup.date] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </div>
          </div>
          
          {expandedDates[dateGroup.date] && (
            <div className="divide-y">
              {dateGroup.chats.map(chat => (
                <div 
                  key={chat.chat_id} 
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => onChatSelect(chat.chat_id)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center">
                      <User size={16} className="mr-2 text-blue-500" />
                      <span className="font-medium">{chat.persona_name}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(chat.timestamp).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'})}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-700 line-clamp-2">{chat.summary}</p>
                  
                  <div className="flex items-center mt-2 text-xs text-gray-500">
                    <MessageSquare size={14} className="mr-1" />
                    <span>{chat.message_count} messages</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ChatListByDate;
